import dayjs from "dayjs";
import { GQL } from "@binale-tech/shared";
import { AppRoutes } from "@domain/routing/routeConstants";
import { getLegalFormCategory } from "./legalFormCategory";
import { type CalendarEvent, type TaxDeadlineInput } from "./types";

/** v1: shift Sat/Sun to the next Monday. `holidays` (YYYY-MM-DD set) is accepted for a future
 * holiday-aware §108 AO shift but unused today. */
export const shiftDueDate = (date: Date, holidays?: ReadonlySet<string>): Date => {
    let d = dayjs(date);
    const isBlocked = (x: dayjs.Dayjs) => x.day() === 0 || x.day() === 6 || !!holidays?.has(x.format("YYYY-MM-DD"));
    while (isBlocked(d)) {
        d = d.add(1, "day");
    }
    return d.toDate();
};

const at = (year: number, month1: number, day: number): Date => shiftDueDate(new Date(year, month1 - 1, day));

// Working shape while building the list - `source`/`stateKey` are stamped uniformly at
// the end (see the final .map()) so every push site below stays untouched.
type DerivedEventDraft = Omit<CalendarEvent, "source" | "stateKey">;

export const taxDeadlines = (input: TaxDeadlineInput): CalendarEvent[] => {
    const { year, legalForm, taxation } = input;
    const cat = getLegalFormCategory(legalForm);
    const events: DerivedEventDraft[] = [];
    const isKust = taxation === GQL.ICompanyTaxation.Kust;

    // 1) UStVA - monthly (default) or quarterly cadence, due 10th of the following period
    // (+1 month under Dauerfristverlängerung). `at(year, month1, day)` already normalizes
    // month1 > 12 via JS Date's native month-overflow rollover
    // (new Date(year, 12, d) === new Date(year+1, 0, d)), so `month + 1 + ext` needs no
    // manual wraparound (SP2, design spec §II.7). Amount attached later in the hook.
    const frequency = input.ustvaFrequency ?? GQL.IUstvaFilingFrequency.Monthly;
    // Keine UStVA: only the annual declaration is filed, so no advance-return deadlines
    if (!isKust && frequency !== GQL.IUstvaFilingFrequency.None) {
        const ext = input.dauerfristverlaengerung ? 1 : 0;
        if (frequency === GQL.IUstvaFilingFrequency.Quarterly) {
            const quarters = [
                { q: 1, endMonth: 3 },
                { q: 2, endMonth: 6 },
                { q: 3, endMonth: 9 },
                { q: 4, endMonth: 12 },
            ];
            for (const { q, endMonth } of quarters) {
                events.push({
                    id: `ustva-${year}-Q${q}`,
                    date: at(year, endMonth + 1 + ext, 10),
                    category: "ustva",
                    titleId: "app.home.calendar.ustvaQuarter",
                    titleValues: { quarter: q },
                    link: AppRoutes.UStVA,
                });
            }
        } else {
            for (let m = 1; m <= 12; m++) {
                events.push({
                    id: `ustva-${year}-${String(m).padStart(2, "0")}`,
                    date: at(year, m + 1 + ext, 10),
                    category: "ustva",
                    titleId: "app.home.calendar.ustva",
                    titleValues: {
                        month: dayjs(new Date(2000, m - 1, 1)).format("MMMM"),
                    },
                    link: AppRoutes.UStVA,
                });
            }
        }
        // Sondervorauszahlung - §47 UStDV, MONTHLY filers only, only when
        // Dauerfristverlängerung is granted. Quarterly filers get the +1 month
        // extension without any Sondervorauszahlung (§46 UStDV) - do not emit this
        // row for them. Date-only in v1 (no amount, §II.11 decision 5).
        if (frequency === GQL.IUstvaFilingFrequency.Monthly && input.dauerfristverlaengerung) {
            events.push({
                id: `sondervz-${year}`,
                date: at(year, 2, 10),
                category: "sondervz",
                titleId: "app.home.calendar.sondervz",
                link: AppRoutes.UStVA,
            });
        }
    }

    // 2) ESt-VZ or KSt-VZ - quarterly 10.03/06/09/12
    if (cat.known) {
        const vzMonths = [3, 6, 9, 12];
        const category = cat.isKapitalgesellschaft ? "vz-kst" : "vz-est";
        const titleId = cat.isKapitalgesellschaft ? "app.home.calendar.vzKst" : "app.home.calendar.vzEst";
        for (const m of vzMonths) {
            events.push({
                id: `${category}-${year}-${String(m).padStart(2, "0")}`,
                date: at(year, m, 10),
                category,
                titleId,
            });
        }
        // 3) GewSt-VZ - quarterly 15.02/05/08/11
        if (cat.isGewerbe) {
            for (const m of [2, 5, 8, 11]) {
                events.push({
                    id: `vz-gewst-${year}-${String(m).padStart(2, "0")}`,
                    date: at(year, m, 15),
                    category: "vz-gewst",
                    titleId: "app.home.calendar.vzGewst",
                });
            }
        }
    }

    // 4) Annual returns (for assessment year = `year`): soft 31.07.(year+1), hard end-Feb.(year+2)
    const soft = at(year + 1, 7, 31);
    const hard = shiftDueDate(
        dayjs(new Date(year + 2, 1, 1))
            .endOf("month")
            .startOf("day")
            .toDate(),
    ); // last day of Feb
    // steuerlichBeraten tri-state (SP2, design spec §II.7/§II.11 decision 6) picks which
    // date is authoritative (§149 Abs. 3 AO extends the deadline when a Steuerberater
    // represents the company): true → hard (StB-extended) date only, false → soft
    // (self-filed) date only, undefined/null (not configured) → both, today's behavior.
    const annual = (id: string, titleId: string) => {
        if (input.steuerlichBeraten === true) {
            events.push({ id, date: hard, category: "annual", titleId });
        } else if (input.steuerlichBeraten === false) {
            events.push({ id, date: soft, category: "annual", titleId });
        } else {
            events.push({ id, date: hard, softDate: soft, category: "annual", titleId });
        }
    };
    if (!isKust) {
        annual(`annual-ust-${year}`, "app.home.calendar.annualUst");
    }
    if (cat.known) {
        annual(
            cat.isKapitalgesellschaft ? `annual-kst-${year}` : `annual-est-${year}`,
            cat.isKapitalgesellschaft ? "app.home.calendar.annualKst" : "app.home.calendar.annualEst",
        );
        if (cat.isGewerbe) {
            annual(`annual-gewst-${year}`, "app.home.calendar.annualGewst");
        }
    }

    // 5) Offenlegung - 12 months after Bilanzstichtag (calendar year → 31.12 of following year).
    // Review fix: the Bilanzstichtag must be the fiscal-year-END that falls in the
    // ASSESSMENT year `year` - it must never be derived by taking some other year's
    // stichtag and clobbering `.year(year)` onto it (that silently breaks both
    // abweichendes Wirtschaftsjahr, e.g. an April-start fiscal year, AND leap-year
    // adjustment, since swapping only the year field doesn't re-derive Feb 28 vs. 29).
    // Instead: the fiscal-year-end date is always "the day before the next occurrence of
    // fiscalYearStart's month/day". For a Jan-1 (calendar-year) start, the fiscal year
    // that ends IN `year` runs Jan 1 `year` .. Dec 31 `year`, so that next occurrence
    // falls in `year + 1`. For any other start month (abweichendes Wirtschaftsjahr, e.g.
    // April 1), the fiscal year ending in `year` already started the previous calendar
    // year, so the next occurrence falls within `year` itself (April 1 `year` - 1 day =
    // March 31 `year`).
    if (cat.offenlegungspflichtig) {
        const fys = dayjs(input.fiscalYearStart ?? "");
        const isCalendarYearStart = fys.isValid() && fys.month() === 0 && fys.date() === 1;
        const bilanzstichtag = fys.isValid()
            ? dayjs(new Date(year + (isCalendarYearStart ? 1 : 0), fys.month(), fys.date())).subtract(1, "day")
            : dayjs(new Date(year, 11, 31));
        events.push({
            id: `offenlegung-${year}`,
            date: shiftDueDate(bilanzstichtag.add(12, "month").toDate()),
            category: "offenlegung",
            titleId: "app.home.calendar.offenlegung",
        });
    }

    // Every derived deadline is `source: "derived"` with `stateKey` == its synthetic id
    // (design spec §14.2) - the id itself is a versioned, persisted-key contract used by
    // SP2 (amounts) and SP3 (per-item state); never renumber it, only extend additively.
    return events.map((e) => ({ ...e, source: "derived" as const, stateKey: e.id }));
};
